import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';
import { FiMapPin, FiLayers, FiChevronDown } from 'react-icons/fi';
import {
  fetchAuthorizedBranches,
  applyActiveBranch,
  applyAllBranches,
  rememberBranchChoice,
  branchLabel,
} from '../utils/authorizedBranches';
import { store } from '../store';
import { differingBranchRole } from './BranchRolesTable';
import { roleLabel } from '../utils/roleLabel';

const ALL = 'all';

const BranchSelector = () => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [branches, setBranches] = useState([]);
  // 'all' sentinel or a branch object.
  const [selected, setSelected] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  // The picker shows the short branch reference (e.g. "Riyadh", "Main") via the
  // shared branchLabel. The customer-facing name is the company commercial
  // name, so it's the same across branches and useless for choosing one — the
  // reference disambiguates them.
  const branchName = branchLabel;
  const isAllSelected = selected === ALL;
  // /auth/me sends all_branches_allowed only with per_branch_roles on: false
  // for a user whose role differs between branches (the server refuses them
  // "All branches"). Absent reads as allowed, so the picker is today's.
  const authUser = store.getState().auth.user;
  const allBranchesAllowed = authUser?.all_branches_allowed !== false;

  useEffect(() => {
    fetchBranches();
  }, []);

  const fetchBranches = async () => {
    try {
      // Only the branches THIS user may switch into (home + multi-branch grants,
      // or every company branch for an admin). Server-scoped, so the list never
      // offers a branch a switch would 403 on. Shared with a job card mounting
      // in the same instant (P03); nothing is cached past the request.
      const garages = await fetchAuthorizedBranches();
      setBranches(garages);

      // Seed the "last real branch" fallback (used as x-garage-id while in
      // All-branches mode so writes still target a concrete branch).
      const homeOrFirst = garages.find((g) => g.is_home) || garages[0];
      if (!localStorage.getItem('lastRealGarageId') && homeOrFirst) {
        localStorage.setItem('lastRealGarageId', homeOrFirst.id);
      }

      // Initial selection: All-branches if that was persisted, else the active
      // branch, else home, else the first allowed one.
      const activeId = localStorage.getItem('activeGarageId');
      // Stale All mode: a user left with at most one branch (a grant was
      // revoked) whose stored picker value is still 'all' can never leave it —
      // this picker is hidden at one branch, and writes in All-scope are
      // refused. Route them back through the same path as a normal pick. It
      // fires only while the stored value is 'all', so the reload cannot loop.
      // The same fallback for a user whose roles became different per branch
      // while in All mode: "All branches" is no longer theirs to use.
      if (
        (garages.length <= 1 || store.getState().auth.user?.all_branches_allowed === false) &&
        homeOrFirst &&
        activeId === ALL
      ) {
        applyBranchChoice(homeOrFirst);
        return;
      }
      if (activeId === ALL) {
        setSelected(ALL);
      } else {
        setSelected(garages.find((g) => g.id === activeId) || homeOrFirst);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      toast.error(t('jc.transfer.loadFailed'));
    }
  };

  // P8: the pick is remembered (per user) so the sign-in gate can pre-select
  // it next login. Read from the store at pick time, not via useSelector:
  // this picker must also work where no Provider exists above it.
  const rememberChoice = (choice) => {
    rememberBranchChoice(store.getState().auth.user?.id, choice);
  };

  // The one branch-pick path: persist the branch and reload so every surface
  // refetches under it.
  const applyBranchChoice = (branch) => {
    setSelected(branch);
    setIsOpen(false);

    rememberChoice(branch.id);
    applyActiveBranch(branch.id);

    // Reload to refetch all data under the new branch context.
    window.location.reload();
  };

  const handleBranchChange = (branch) => {
    applyBranchChoice(branch);
  };

  const handleSelectAll = () => {
    setSelected(ALL);
    setIsOpen(false);
    rememberChoice(ALL);
    applyAllBranches();
    window.location.reload();
  };

  // Don't show if only one branch or no branches
  if (!branches || branches.length <= 1) {
    return null;
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-2.5 sm:px-3.5 py-2.5 bg-surface border border-edge-strong rounded-lg hover:bg-surface-2 transition-colors"
      >
        {isAllSelected ? (
          <FiLayers className="text-accent" />
        ) : (
          <FiMapPin className="text-accent" />
        )}
        <span
          className="font-medium text-sm text-content hidden sm:inline max-w-56 truncate"
          title={selected && !isAllSelected ? branchName(selected) : undefined}
        >
          {isAllSelected
            ? isAr
              ? 'كل الفروع'
              : 'All branches'
            : selected
              ? branchName(selected)
              : isAr
                ? 'اختر الفرع'
                : 'Select Branch'}
        </span>
        <FiChevronDown
          className={`text-content-muted transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />

          {/* Dropdown */}
          <div className="absolute end-0 mt-2 w-64 max-w-[calc(100vw-2rem)] bg-surface border border-edge rounded-xl shadow-xl z-50">
            <div className="p-2">
              <div className="px-3 py-2 text-xs font-semibold text-content-muted uppercase">
                {isAr ? 'اختر الفرع' : 'Select Branch'}
              </div>

              {/* All branches — company-wide view across every allowed branch */}
              {allBranchesAllowed && (
                <>
                  <button
                    onClick={handleSelectAll}
                    className={`w-full text-start px-3 py-2 rounded-lg transition-colors mb-0.5 ${
                      isAllSelected
                        ? 'bg-accent-soft text-accent-hover dark:text-accent font-medium'
                        : 'hover:bg-surface-2 text-content'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <FiLayers className={isAllSelected ? 'text-accent' : 'text-content-muted'} />
                      <div className="min-w-0">
                        <div className="font-medium text-sm truncate">
                          {isAr ? 'كل الفروع' : 'All branches'}
                        </div>
                        <div className="text-xs text-content-muted">
                          {t('mb.allBranchesHint')}
                        </div>
                      </div>
                    </div>
                  </button>

                  <div className="my-1 border-t border-edge" />
                </>
              )}

              {branches.map((branch) => (
                <button
                  key={branch.id}
                  onClick={() => handleBranchChange(branch)}
                  className={`w-full text-start px-3 py-2 rounded-lg transition-colors ${
                    !isAllSelected && selected?.id === branch.id
                      ? 'bg-accent-soft text-accent-hover dark:text-accent font-medium'
                      : 'hover:bg-surface-2 text-content'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <FiMapPin
                      className={
                        !isAllSelected && selected?.id === branch.id
                          ? 'text-accent'
                          : 'text-content-muted'
                      }
                    />
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{branchName(branch)}</div>
                      {branch.is_home && (
                        <div className="text-xs text-content-muted">
                          {t('mb.homeBranch')}
                        </div>
                      )}
                      {differingBranchRole(branch, authUser?.default_role) && (
                        <div className="text-xs text-content-muted">
                          {t('ubr.roleInBranch', {
                            role: roleLabel(differingBranchRole(branch, authUser.default_role)),
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BranchSelector;
